// ─── Livestream shared base ───────────────────────────────────────
// Logic chung cho TaixiuLivestreamModal + XocdiaLivestreamModal:
//   - SSE stream (connect/stop)
//   - Logs state + addLog + exportLogs
//   - Countdown effect
//   - Session state chung (sessionId, countdown, status, coins, jackpot, dealerName, odds, betSummary, history)
//
// Mỗi modal game-specific sẽ:
//   - định nghĩa GameResult + parseResult riêng
//   - implement parseSessionData riêng (dùng parseResult của game đó)
//   - render UI riêng (dice vs đĩa đỏ/trắng)

import React from 'react';
import { siteConfig } from '../../lib/config';

// ─── Shared types ──────────────────────────────────────────────────

export interface LivestreamLogEntry {
  direction: 'SEND' | 'RECV' | 'INFO' | 'ERROR';
  timestamp: string;
  cmd: number | string;
  label: string;
  raw: unknown;
}

export interface LivestreamAccount {
  _id?: string;
  username: string;
  displayname: string;
  config?: string;
}

export interface LivestreamModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: LivestreamAccount | null;
}

/** Entry lịch sử — result là kiểu game-specific (DiceResult | XocdiaResult) */
export interface HistoryEntry<R> {
  sessionId: number;
  result: R;
  resultRaw: string;
}

/** Session state chung — R là kiểu result game-specific */
export interface SessionState<R> {
  sessionId: number | null;
  countdown: number;
  timeBet: number;
  endBettingTime: number | null;
  stopBetSecond: number;
  status: string; // BETTING | WAITING | ROLLING | ENDED
  coins: number[];
  jackpot: number;
  dealerName: string | null;
  odds: Array<{ name: string; rate: number }> | null;
  betSummary: Array<{ eid: string; v: number; uC: number }> | null;
  totalPlayers: number | null;
  lastResult: R | null;
  betTypeResult: string | null;
  history: HistoryEntry<R>[];
}

export const initialSession = <R,>(): SessionState<R> => ({
  sessionId: null,
  countdown: 0,
  timeBet: 35,
  endBettingTime: null,
  stopBetSecond: 3,
  status: '',
  coins: [],
  jackpot: 0,
  dealerName: null,
  odds: null,
  betSummary: null,
  totalPlayers: null,
  lastResult: null,
  betTypeResult: null,
  history: [],
});

// ─── Shared helpers ────────────────────────────────────────────────

export function formatCoin(value: number): string {
  if (value >= 1000000) return `${value / 1000000}M`;
  if (value >= 1000) return `${value / 1000}K`;
  return value.toString();
}

export function formatJackpot(value: number): string {
  return value.toLocaleString('vi-VN');
}

/** Parse typegame từ account.config (vd: "[TX]..." → "TX") */
export function parseTypegame(account: LivestreamAccount | null): string {
  const configStr = account?.config ?? '';
  const match = configStr.match(/^\[([A-Z]+)\]/);
  return match?.[1] ?? '?';
}

/** Trích payload từ message RECV [5, {...}] — trả null nếu không phải */
export function extractExtensionPayload(entry: LivestreamLogEntry): Record<string, unknown> | null {
  if (entry.direction !== 'RECV' || !entry.raw) return null;
  const raw = entry.raw as unknown[];
  if (!Array.isArray(raw) || raw[0] !== 5 || typeof raw[1] !== 'object') return null;
  return raw[1] as Record<string, unknown>;
}

// ─── Shared hook: SSE stream + logs + countdown ────────────────────

export type StreamStatus = 'idle' | 'connecting' | 'running' | 'done' | 'failed';

/**
 * Hook chung cho livestream modal:
 *   - logs state + addLog + clearLogs + exportLogs
 *   - SSE stream (start/stop) — gọi onLog cho mỗi entry
 *   - countdown effect (đếm ngược khi BETTING)
 *
 * Trả về refs/state cần thiết để modal game-specific dùng.
 */
export function useLivestreamStream<R>(
  account: LivestreamAccount | null,
  isOpen: boolean,
  onLog: (entry: LivestreamLogEntry) => void,
) {
  const [logs, setLogs] = React.useState<LivestreamLogEntry[]>([]);
  const [status, setStatus] = React.useState<StreamStatus>('idle');
  const [showLogs, setShowLogs] = React.useState(true);
  const eventSourceRef = React.useRef<EventSource | null>(null);
  const logsEndRef = React.useRef<HTMLDivElement | null>(null);
  const onLogRef = React.useRef(onLog);
  React.useEffect(() => { onLogRef.current = onLog; }, [onLog]);
  const retryTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = React.useRef(0);
  // true khi modal còn mở / còn muốn xem stream này — TL bắt tay 2 lớp có thể lâu
  // hơn vài giây, "chưa start" lúc này là tạm thời, không nên bỏ cuộc vĩnh viễn.
  const wantStreamRef = React.useRef(false);

  const addLog = React.useCallback((entry: LivestreamLogEntry) => {
    setLogs((prev) => [...prev, entry]);
  }, []);

  const connect = React.useCallback(() => {
    if (!account?._id) return;
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setStatus('connecting');
    const url = `${siteConfig.apiUrl}/api/v1/sunwin/livestream/${account._id}/stream`;
    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as LivestreamLogEntry;
        retryCountRef.current = 0;
        addLog(data);
        onLogRef.current?.(data);
        if (data.cmd === 'DONE') setStatus('running');
        else if (data.cmd === 'FAILED' || data.direction === 'ERROR') setStatus('failed');
        else if (data.cmd === 'AUTH_SUCCESS' || data.cmd === 'PING_START') setStatus('running');
      } catch (err) {
        console.error('Failed to parse SSE data:', err);
      }
    };
    // Retry với backoff thay vì bỏ cuộc ngay ở lỗi đầu tiên — server có thể vẫn
    // đang start, 400 lúc này là tạm thời. Chỉ dừng hẳn khi modal đóng (stopStream).
    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;

      let alreadyDone = false;
      setStatus((prev) => {
        if (prev === 'done') alreadyDone = true;
        return prev;
      });
      if (alreadyDone || !wantStreamRef.current) return;

      addLog({
        direction: 'ERROR',
        timestamp: new Date().toISOString(),
        cmd: 'CONNECTION_ERROR',
        label: 'Mất kết nối với máy chủ API — đang thử lại...',
        raw: {},
      });
      setStatus('failed');

      const delay = Math.min(1000 * 2 ** retryCountRef.current, 10000);
      retryCountRef.current += 1;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (wantStreamRef.current) connect();
      }, delay);
    };
  }, [account, addLog]);

  const startStream = React.useCallback(() => {
    if (!account?._id) return;
    wantStreamRef.current = true;
    retryCountRef.current = 0;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    setLogs([]);
    connect();
  }, [account, connect]);

  const stopStream = React.useCallback(() => {
    wantStreamRef.current = false;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Auto-scroll
  React.useEffect(() => {
    if (isOpen && logsEndRef.current && showLogs) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen, showLogs]);

  // Start on open, cleanup on close
  React.useEffect(() => {
    if (isOpen && account) {
      startStream();
    }
    return () => {
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const exportLogs = React.useCallback((gameName: string, typegame: string) => {
    if (logs.length === 0) return;
    const lines: string[] = [];
    lines.push(`# Livestream WS Log Export`);
    lines.push(`# Account: ${account?.username ?? ''}`);
    lines.push(`# Game: ${gameName} (${typegame})`);
    lines.push(`# Status: ${status}`);
    lines.push(`# Exported: ${new Date().toISOString()}`);
    lines.push(`# Total messages: ${logs.length}`);
    lines.push('');
    lines.push('='.repeat(80));
    lines.push('');
    for (const log of logs) {
      const time = log.timestamp ? new Date(log.timestamp).toLocaleTimeString('vi-VN', { hour12: false }) : '';
      const dir = log.direction === 'SEND' ? 'SEND' : log.direction === 'RECV' ? 'RECV' : log.direction === 'ERROR' ? 'ERR ' : 'INFO';
      lines.push(`[${time}] ${dir} | ${log.label}`);
      if (log.raw !== null && log.raw !== undefined) {
        try {
          const rawStr = typeof log.raw === 'string' ? log.raw : JSON.stringify(log.raw, null, 2);
          lines.push(rawStr);
        } catch {
          lines.push(String(log.raw));
        }
      }
      lines.push('-'.repeat(80));
    }
    const content = lines.join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url;
    a.download = `livestream-${account?.username ?? 'unknown'}-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [logs, status, account]);

  return {
    logs,
    setLogs,
    status,
    showLogs,
    setShowLogs,
    logsEndRef,
    startStream,
    stopStream,
    exportLogs,
  };
}

/**
 * Hook countdown — đếm ngược khi status === 'BETTING'.
 * Cập nhật SessionState qua setSession.
 */
export function useCountdown<R>(
  setSession: React.Dispatch<React.SetStateAction<SessionState<R>>>,
) {
  React.useEffect(() => {
    const timer = setInterval(() => {
      setSession((prev) => {
        if (prev.status === 'BETTING' && prev.countdown > 0) {
          const newCountdown = prev.countdown - 1;
          if (newCountdown <= prev.stopBetSecond && newCountdown > 0) {
            return { ...prev, countdown: newCountdown, status: 'WAITING' };
          }
          if (newCountdown === 0) {
            return { ...prev, countdown: 0, status: 'WAITING' };
          }
          return { ...prev, countdown: newCountdown };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [setSession]);
}

// ─── Shared UI helpers (color classes) ─────────────────────────────

export function statusColorClass(status: StreamStatus): string {
  return status === 'failed'
    ? 'bg-red-500'
    : status === 'running' || status === 'done'
      ? 'bg-emerald-500'
      : status === 'connecting'
        ? 'bg-amber-400'
        : 'bg-slate-400';
}

export function statusPingColorClass(status: StreamStatus): string {
  return status === 'failed'
    ? 'bg-red-400'
    : status === 'running' || status === 'done'
      ? 'bg-emerald-400'
      : status === 'connecting'
        ? 'bg-amber-300'
        : 'bg-slate-300';
}

export function countdownColorClass(session: SessionState<unknown>): string {
  return session.status === 'WAITING'
    ? 'text-red-400'
    : session.countdown > 3
      ? 'text-emerald-400'
      : session.countdown > 0
        ? 'text-amber-400'
        : 'text-slate-500';
}

export function sessionStatusColorClass(status: string): string {
  return status === 'BETTING' ? 'text-emerald-400' :
    status === 'WAITING' ? 'text-amber-400' :
    status === 'ROLLING' ? 'text-purple-400' :
    status === 'ENDED' ? 'text-slate-400' :
    'text-slate-500';
}